import { Amount, ItineraryCandidate } from './CreateItineraryForm';
import {
  Budget as NetworkBudget,
  Itinerary as NetworkItinerary,
  Amount as NetworkAmount,
  ItineraryFeature as NetworkFeature,
} from '../../../graphql/datamodel.gen';
import moment from 'moment';
import {
  BudgetCandidate,
  BudgetInstance,
} from '../CreateBudget/CreateBudgetForm';
import { Feature } from '../CreateItineraryFeature/CreateItineraryFeatureForm';

export interface ItineraryJSON
  extends Omit<
    ItineraryCandidate,
    'departureTime' | 'arrivalTime' | 'presentationTime' | 'vehicleCategory'
  > {
  departureTime: string;
  arrivalTime: string;
  presentationTime: string;
  vehicleCategoryId?: string;
}

export type BudgetJSON = Omit<
  BudgetInstance,
  'creationDate' | 'itineraries' | 'id' | 'readableId'
> & {
  id?: string;
  readableId?: string;
  creationDate: string;
  validityPeriodStart: string;
  validityPeriodEnd: string;
  itineraries: ItineraryJSON[];
  contactId?: string;
};

export const itineraryToJSON = (
  itinerary: ItineraryCandidate,
): ItineraryJSON => {
  const {
    vehicleCategory,
    // @ts-ignore
    vehicleModelId,
    // @ts-ignore
    vehicleTypeId,
    // @ts-ignore
    vehicleBrandId,
    // @ts-ignore
    vehicleCapacity,
    ...cleanedItinerary
  } = itinerary;

  const itineraryJSON: ItineraryJSON = {
    ...cleanedItinerary,
    // @ts-ignore
    vehicleCategoryId: vehicleCategory?.id || itinerary.vehicleCategoryId,
    departureTime: cleanedItinerary.departureTime.toISOString(),
    arrivalTime: cleanedItinerary.arrivalTime.toISOString(),
    presentationTime: cleanedItinerary.presentationTime.toISOString(),
  };

  return itineraryJSON;
};

export const budgetInstanceToJSON = (
  budgetCandidate: BudgetInstance | BudgetCandidate,
): BudgetJSON => {
  return {
    ...budgetCandidate,
    contactId: (budgetCandidate as BudgetCandidate).contactId,
    validityPeriodStart: budgetCandidate.validityPeriod[0].toISOString(),
    validityPeriodEnd: budgetCandidate.validityPeriod[1].toISOString(),
    creationDate: budgetCandidate.creationDate.toISOString(),
    itineraries: budgetCandidate.itineraries.map(itineraryToJSON),
  };
};

export const instantiateAmountFromNetworkResponse = (
  networkAmount: NetworkAmount,
): Amount => {
  const { __typename, ...sanitized } = { ...networkAmount };
  return sanitized;
};

export const instantiateFeatureFromNetworkResponse = (
  networkFeature: NetworkFeature,
): Feature => {
  const { __typename, ...sanitized } = { ...networkFeature };
  return sanitized;
};

export const instantiateItineraryFromNetworkResponse = (
  networkItinerary: NetworkItinerary,
): ItineraryCandidate => {
  let { __typename, ...sanitizedCopy } = {
    ...networkItinerary,
  };

  return {
    ...sanitizedCopy,
    realized: networkItinerary.realized,
    driverFeatures: [
      ...(networkItinerary.driverFeatures || []).map(
        instantiateFeatureFromNetworkResponse,
      ),
    ],
    vehicleFeatures: [
      ...(networkItinerary.vehicleFeatures || []).map(
        instantiateFeatureFromNetworkResponse,
      ),
    ],
    amounts: [
      ...(networkItinerary.amounts || []).map(
        instantiateAmountFromNetworkResponse,
      ),
    ],
    departureTime: moment(networkItinerary.departureTime),
    arrivalTime: moment(networkItinerary.arrivalTime),
    presentationTime: moment(networkItinerary.presentationTime),
  };
};

export const instantiateBudgetFromNetworkResponse = (
  networkBudget: NetworkBudget,
): BudgetInstance => {
  return {
    ...networkBudget,
    itineraries: networkBudget.itineraries.map(
      instantiateItineraryFromNetworkResponse,
    ),
    validityPeriod: [
      moment(networkBudget.validityPeriodStart),
      moment(networkBudget.validityPeriodEnd),
    ],
    creationDate: moment(networkBudget.creationDate),
    client: networkBudget.client.id,
    status: networkBudget.status.code,
    company: networkBudget.company.code,
    metThrough: networkBudget.metThrough?.code,
    requestSource: networkBudget.requestSource?.code,
  };
};
